import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Link, List, ListItem, ListItemIcon,
    ListItemText, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Trans, useTranslation } from 'react-i18next';
import { AccountingService } from '../../services/accounting.service';
import { useToast } from '../../context/ToastContext';
import { IYearClose, IYearClosingStatus } from '../../interfaces/accounting.interface';
import { formatMoney } from './money';
import { dayLabel, monthName, reopenedNote } from './closingLabels';

interface Props {
    institutionId: string;
    isAdmin: boolean;
    /** Bumped by the page after a month is closed or reopened, so the year's check is re-read. */
    refreshKey: number;
    /** Called after a year is closed or reopened, so the page re-reads the months. */
    onChanged: () => void;
}

/**
 * Fiscal year-end close: once every month of the year is closed, moves the year's profit or loss into
 * Retained earnings with one closing entry. Only an InstitutionAdmin can reopen, latest year first.
 */
const YearEndClose: React.FC<Props> = ({ institutionId, isAdmin, refreshKey, onChanged }) => {
    const { t } = useTranslation(['accounting', 'common']);
    const { showSuccess, showApiError } = useToast();
    const [status, setStatus] = useState<IYearClosingStatus | null>(null);
    const [confirmClose, setConfirmClose] = useState(false);
    const [reopening, setReopening] = useState<IYearClose | null>(null);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!institutionId) return;
        try {
            setStatus(await AccountingService.yearClosingStatus(institutionId));
        } catch (error) {
            showApiError(error, t('yearEnd.loadFailed'));
        }
    }, [institutionId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const range = (start: string, end: string) => t('closing.range', { start: dayLabel(start), end: dayLabel(end) });

    const result = (netProfit: number) => netProfit >= 0
        ? t('yearEnd.profitAmount', { amount: formatMoney(netProfit) })
        : t('yearEnd.lossAmount', { amount: formatMoney(-netProfit) });

    const close = async () => {
        if (!status?.next) return;
        setBusy(true);
        try {
            await AccountingService.closeYear(institutionId, status.next.end);
            showSuccess(t('closing.isClosed', { label: status.next.label }));
            onChanged();
        } catch (error) {
            showApiError(error, t('yearEnd.closeFailed'));
        } finally {
            setConfirmClose(false);
            setBusy(false);
            await load();
        }
    };

    const reopen = async () => {
        if (!reopening) return;
        setBusy(true);
        try {
            await AccountingService.reopenYear(institutionId, reopening.yearEnd, reason.trim());
            showSuccess(t('closing.isOpenAgain', { label: reopening.label }));
            setReopening(null);
            setReason('');
            onChanged();
        } catch (error) {
            showApiError(error, t('yearEnd.reopenFailed'));
        } finally {
            setBusy(false);
            await load();
        }
    };

    if (!status) return null;

    // Describe the year actually being closed: after the first close the sequence, not the setting, decides.
    const startMonth = status.next ? Number(status.next.start.slice(5, 7)) : status.fiscalYearStartMonth;
    const endMonth = ((startMonth + 10) % 12) + 1;
    const latestClosed = status.history.find(h => h.status === 'CLOSED');
    const settingDrifted = !!latestClosed && status.fiscalYearStartMonth !== startMonth;
    const leavePolicies = isAdmin ? <Link component={RouterLink} to="/leave-policy" /> : <span />;

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>{t('yearEnd.title')}</Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
                <Trans t={t} i18nKey="yearEnd.intro" components={{ link: leavePolicies }}
                       values={{ start: monthName(startMonth), end: monthName(endMonth) }} />
            </Alert>
            {settingDrifted && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    {t('yearEnd.settingDrifted', {
                        setting: monthName(status.fiscalYearStartMonth), start: monthName(startMonth), end: monthName(endMonth),
                    })}
                </Alert>
            )}

            {status.next ? (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {t('yearEnd.heading', { label: status.next.label, range: range(status.next.start, status.next.end) })}
                    </Typography>
                    <List dense>
                        {status.checks.map(c => (
                            <ListItem key={c.key} disableGutters>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    {c.ok
                                        ? <CheckCircleIcon color="success" fontSize="small" titleAccess={t('closing.done')} />
                                        : <CancelIcon color="error" fontSize="small" titleAccess={t('closing.notDone')} />}
                                </ListItemIcon>
                                <ListItemText primary={c.label} secondary={c.ok ? null : c.detail} />
                            </ListItem>
                        ))}
                    </List>
                    {status.preview && (
                        <Typography sx={{ mb: 2 }}>
                            {t('yearEnd.preview', {
                                income: formatMoney(status.preview.totalIncome),
                                expenses: formatMoney(status.preview.totalExpense),
                                result: result(status.preview.netProfit),
                            })}
                        </Typography>
                    )}
                    <Button variant="contained" disabled={!status.canClose} onClick={() => setConfirmClose(true)}>
                        {t('closing.closeLabel', { label: status.next.label })}
                    </Button>
                </Paper>
            ) : (
                <Typography color="text.secondary" sx={{ mb: 3 }}>{t('closing.nothingToClose')}</Typography>
            )}

            {status.history.length > 0 && (
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('yearEnd.fiscalYear')}</TableCell>
                                <TableCell>{t('common:fields.status')}</TableCell>
                                <TableCell align="right">{t('figures.income')}</TableCell>
                                <TableCell align="right">{t('figures.expenses')}</TableCell>
                                <TableCell align="right">{t('figures.profitLoss')}</TableCell>
                                <TableCell>{t('closing.closedColumn')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {status.history.map(h => (
                                <TableRow key={h.id}>
                                    <TableCell>
                                        {h.label}
                                        <Typography variant="caption" display="block" color="text.secondary">
                                            {range(h.yearStart, h.yearEnd)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {h.status === 'CLOSED'
                                            ? <Chip size="small" color="success" label={t('closing.closedChip')} />
                                            : <Chip size="small" label={t('closing.reopenedChip')} />}
                                        {h.status === 'REOPENED' && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                {reopenedNote(h)}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">{formatMoney(h.figures.totalIncome)}</TableCell>
                                    <TableCell align="right">{formatMoney(h.figures.totalExpense)}</TableCell>
                                    <TableCell align="right">{formatMoney(h.figures.netProfit)}</TableCell>
                                    <TableCell>
                                        {h.closedBy
                                            ? t('closing.dateBy', { date: new Date(h.closedAt).toLocaleDateString(), name: h.closedBy })
                                            : new Date(h.closedAt).toLocaleDateString()}
                                        {h.journalEntryId && (
                                            <Link component={RouterLink} to={`/accounting/entries/${h.journalEntryId}`}
                                                  sx={{ display: 'block' }}>
                                                {t('yearEnd.closingEntry')}
                                            </Link>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {isAdmin && h.id === latestClosed?.id && (
                                            <Button size="small" onClick={() => setReopening(h)}>{t('closing.reopen')}</Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}

            <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
                <DialogTitle>{t('closing.closeConfirmTitle', { label: status.next?.label ?? '' })}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {status.preview
                            ? t('yearEnd.confirmBodyWithResult', {
                                result: result(status.preview.netProfit), date: status.next ? dayLabel(status.next.end) : '',
                            })
                            : t('yearEnd.confirmBody', { date: status.next ? dayLabel(status.next.end) : '' })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmClose(false)}>{t('common:actions.cancel')}</Button>
                    <Button variant="contained" onClick={close} disabled={busy}>{t('yearEnd.closeYear')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={reopening !== null} onClose={() => setReopening(null)} fullWidth maxWidth="sm">
                <DialogTitle>{t('closing.reopenLabel', { label: reopening?.label ?? '' })}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        {t('yearEnd.reopenIntro')}
                    </Typography>
                    <TextField id="reopen-year-reason" label={t('common:fields.reason')} fullWidth multiline minRows={2} value={reason}
                               inputProps={{ maxLength: 500 }} onChange={e => setReason(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReopening(null)}>{t('common:actions.cancel')}</Button>
                    <Button variant="contained" color="warning" onClick={reopen} disabled={busy || !reason.trim()}>
                        {t('closing.reopenLabel', { label: reopening?.label ?? '' })}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default YearEndClose;
